import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import Wordmark from "@/sections/Wordmark";
import PlatformMegaMenu from "@/sections/nav/PlatformMegaMenu";

const navItems = [
    { to: "/", label: "Home" },
    { to: "/about", label: "About" },
    { to: "/pricing", label: "Pricing" },
    { to: "/contact", label: "Contact" },
];

const Header = () => {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [megaOpen, setMegaOpen] = useState(false);
    const { pathname } = useLocation();
    const megaButtonRef = useRef(null);
    const megaMenuRef = useRef(null);
    const megaCloseTimer = useRef(null);

    // Scroll to top when clicking a nav link that points to the current page.
    // (react-router doesn't re-fire route change on same-path clicks, so the
    // global ScrollHandler in App.js never triggers.)
    const handleSamePageNav = (to) => {
        if (to === pathname || (to === "/" && pathname === "/")) {
            window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
        }
    };

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 30);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        document.body.style.overflow = open ? "hidden" : "";
        return () => { document.body.style.overflow = ""; };
    }, [open]);

    // Close mega on outside click + Escape
    useEffect(() => {
        if (!megaOpen) return;
        const handler = (e) => {
            const inBtn = megaButtonRef.current?.contains(e.target);
            const inMenu = megaMenuRef.current?.contains(e.target);
            if (!inBtn && !inMenu) setMegaOpen(false);
        };
        const onKey = (e) => { if (e.key === "Escape") setMegaOpen(false); };
        document.addEventListener("mousedown", handler);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", handler);
            document.removeEventListener("keydown", onKey);
        };
    }, [megaOpen]);

    // Close mega when route changes
    useEffect(() => { setMegaOpen(false); }, [pathname]);

    const openMega  = () => { clearTimeout(megaCloseTimer.current); setMegaOpen(true); };
    const closeMega = () => { megaCloseTimer.current = setTimeout(() => setMegaOpen(false), 120); };

    return (
        <>
            <motion.header
                initial={{ y: -20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                data-testid="site-header"
                className={`sticky top-0 z-50 transition-colors duration-300 ${
                    scrolled
                        ? "bg-[var(--cream)]/92 backdrop-blur-md border-b border-[var(--border)]"
                        : "bg-transparent border-b border-transparent"
                }`}
            >
                <div className="max-w-[1280px] mx-auto px-5 md:px-8 h-[68px] flex items-center justify-between gap-6">
                    <Link to="/" onClick={() => { setOpen(false); handleSamePageNav("/"); }} data-testid="logo-link" className="flex items-center shrink-0">
                        <Wordmark />
                    </Link>

                    <nav className="hidden md:flex items-center gap-8 text-[14px]">
                        {navItems.map((n) => {
                            const isActive = n.to === "/" ? pathname === "/" : pathname.startsWith(n.to);

                            // Platform → mega menu trigger
                            if (n.mega) {
                                return (
                                    <div
                                        key={n.to}
                                        ref={megaButtonRef}
                                        className="relative"
                                        onMouseEnter={openMega}
                                        onMouseLeave={closeMega}
                                    >
                                        <button
                                            type="button"
                                            onClick={() => setMegaOpen((v) => !v)}
                                            aria-expanded={megaOpen}
                                            aria-haspopup="true"
                                            data-testid={`nav-${n.label.toLowerCase()}`}
                                            className={`relative link-grow inline-flex items-center gap-1.5 transition-colors ${
                                                isActive || megaOpen ? "accent-blue font-medium" : ""
                                            }`}
                                        >
                                            {n.label}
                                            <motion.i
                                                animate={{ rotate: megaOpen ? 180 : 0 }}
                                                transition={{ duration: 0.25 }}
                                                className="fa-solid fa-chevron-down text-[9px]"
                                            />
                                            {isActive && (
                                                <motion.span
                                                    layoutId="nav-active-underline"
                                                    className="absolute -bottom-1 left-0 right-4 h-[2px] bg-[var(--blue)] rounded-full"
                                                    transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                                                />
                                            )}
                                        </button>
                                    </div>
                                );
                            }

                            return (
                                <Link
                                    key={n.to}
                                    to={n.to}
                                    onClick={() => handleSamePageNav(n.to)}
                                    aria-current={isActive ? "page" : undefined}
                                    data-testid={`nav-${n.label.toLowerCase()}`}
                                    className={`relative link-grow transition-colors ${
                                        isActive ? "accent-blue font-medium" : ""
                                    }`}
                                >
                                    {n.label}
                                    {isActive && (
                                        <motion.span
                                            layoutId="nav-active-underline"
                                            className="absolute -bottom-1 left-0 right-0 h-[2px] bg-[var(--blue)] rounded-full"
                                            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                                        />
                                    )}
                                </Link>
                            );
                        })}
                    </nav>

                    <div className="flex items-center gap-3 shrink-0">
                        <a
                            href="https://calendly.com/d/d3nd-zhb-2fr/lexafide-demo-discussion-30-minute-meeting-web"
                            target="_blank"
                            rel="noreferrer"
                            data-testid="header-demo-btn"
                            className="btn-primary text-[13px] hidden sm:inline-flex items-center gap-2 py-2.5 px-5"
                        >
                            Book a demo
                            <i className="fa-solid fa-arrow-right text-[10px]" />
                        </a>
                        <button
                            type="button"
                            data-testid="mobile-menu-toggle"
                            onClick={() => setOpen((v) => !v)}
                            aria-label={open ? "Close menu" : "Open menu"}
                            className="md:hidden w-10 h-10 rounded-full border border-[var(--border-strong)] flex items-center justify-center text-[var(--ink)] hover:bg-[var(--cream-2)] transition-colors"
                        >
                            <span className="relative w-4 h-3 block">
                                <motion.span animate={{ rotate: open ? 45 : 0, y: open ? 5 : 0 }} transition={{ duration: 0.3 }} className="absolute left-0 top-0 w-4 h-px bg-current" />
                                <motion.span animate={{ opacity: open ? 0 : 1 }} transition={{ duration: 0.2 }} className="absolute left-0 top-[5px] w-4 h-px bg-current" />
                                <motion.span animate={{ rotate: open ? -45 : 0, y: open ? -5 : 0 }} transition={{ duration: 0.3 }} className="absolute left-0 top-[10px] w-4 h-px bg-current" />
                            </span>
                        </button>
                    </div>
                </div>

                {/* Mega menu — anchored to header, centered on the page container */}
                <div
                    ref={megaMenuRef}
                    onMouseEnter={openMega}
                    onMouseLeave={closeMega}
                    className="absolute left-0 right-0 top-full flex justify-center pointer-events-none"
                >
                    <div className="w-full max-w-[1280px] px-5 md:px-8 pointer-events-auto">
                        <PlatformMegaMenu open={megaOpen} onClose={() => setMegaOpen(false)} />
                    </div>
                </div>
            </motion.header>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        data-testid="mobile-drawer"
                        className="fixed inset-0 z-[60] md:hidden"
                    >
                        <div onClick={() => setOpen(false)} className="absolute inset-0 bg-[var(--ink)]/40 backdrop-blur-sm" />
                        <motion.div
                            initial={{ x: "100%" }}
                            animate={{ x: 0 }}
                            exit={{ x: "100%" }}
                            transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
                            className="absolute right-0 top-0 bottom-0 w-[86%] max-w-sm bg-[var(--paper)] border-l border-[var(--border-strong)] flex flex-col pt-20 pb-8 px-7 overflow-y-auto"
                        >
                            <nav className="flex flex-col gap-1">
                                {navItems.map((n, i) => {
                                    const isActive = n.to === "/" ? pathname === "/" : pathname.startsWith(n.to);
                                    return (
                                        <motion.div
                                            key={n.to}
                                            initial={{ opacity: 0, x: 20 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            transition={{ duration: 0.4, delay: 0.1 + i * 0.06 }}
                                        >
                                            <Link
                                                to={n.to}
                                                onClick={() => { setOpen(false); handleSamePageNav(n.to); }}
                                                aria-current={isActive ? "page" : undefined}
                                                data-testid={`mobile-nav-${n.label.toLowerCase()}`}
                                                className="group flex items-center justify-between py-5 border-b border-[var(--border)]"
                                            >
                                                <span
                                                    className={`font-display font-bold text-2xl uppercase tracking-tight transition-colors ${
                                                        isActive ? "accent-blue" : "text-[var(--ink)] group-hover:accent-blue"
                                                    }`}
                                                >
                                                    {n.label}
                                                </span>
                                                <i
                                                    className={`fa-solid fa-arrow-right transition-colors ${
                                                        isActive ? "accent-blue" : "text-[var(--ink-dim)] group-hover:accent-blue"
                                                    }`}
                                                />
                                            </Link>
                                        </motion.div>
                                    );
                                })}
                            </nav>
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: 0.4 }}
                                className="mt-auto pt-10"
                            >
                                <a
                                    href="https://calendly.com/d/d3nd-zhb-2fr/lexafide-demo-discussion-30-minute-meeting-web"
                                    target="_blank"
                                    rel="noreferrer"
                                    onClick={() => setOpen(false)}
                                    data-testid="mobile-cta-btn"
                                    className="btn-primary w-full flex items-center justify-center gap-2 text-sm"
                                >
                                    Book a 30-min demo
                                    <i className="fa-solid fa-arrow-right text-[10px]" />
                                </a>
                                <p className="font-mono text-[10px] tracking-widest text-[var(--muted)] uppercase text-center mt-6">
                                    200k+ minutes handled by AI this month
                                </p>
                            </motion.div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};

export default Header;
